import { z } from 'zod';

import {
  HotelPlannerPlaceSchema,
  type HotelPlannerSelection,
  type HotelResultsPageRequest,
  type HotelResultsPageResult,
  type HotelSearchPreviewResult,
} from './hotel_planner_schema.js';
import type { HotelDetailsResult } from './hotel_details_schema.js';

export const HotelPlaceOptionSchema = z
  .object({
    placeId: z.string().min(1),
    /** Ref opaco do motor de busca do voll-places (`google:<placeId>`), presente
     *  só nas linhas vindas de `GET /hotel/places` (por termo ou coordenada). O
     *  `placeId` segue CRU em todos os caminhos (sugestões, favoritas) — o ref
     *  viaja em campo próprio para nenhum leitor precisar normalizar prefixo. */
    ref: z.string().min(1).optional(),
    title: z.string().min(1),
    address: z.string().optional(),
    subtitle: z.string().optional(),
    /** Tipos do Google da predição (`locality`, `lodging`, `route`, ...) — dizem
     *  O QUE o lugar é, sem resolver o ref. Viram a primeira metade da segunda
     *  linha (`tipo · endereço`) e escolhem o glifo. Ausentes nas fontes que não
     *  os têm (sugestões da customer-api). */
    types: z.array(z.string().min(1)).optional(),
    classification: z.string().optional(),
    /** Nó completo com coordenada. SEMPRE presente em sugestões/favoritas (a
     *  fonte já o tem); AUSENTE nas linhas de busca do motor novo — a fase de
     *  busca devolve refs sem detalhe, e é o PICK que hidrata (`placeRef` na
     *  seleção, resolvido pelo BFF). Resolver N candidatos na listagem seria
     *  pagar N details por tecla. */
    searchPlace: HotelPlannerPlaceSchema.optional(),
    /** Janela da BUSCA que gravou o recente (só linhas `recent` a carregam):
     *  par completo ou nada — meio período não pré-preenche calendário. Mesmo
     *  formato `z.iso.date` do planner (`checkin`/`checkout`, nunca camelCase
     *  de Date). `guests` é a ocupação total pedida. Opcionais porque recentes
     *  pré-janela (e toda linha `favorite`) seguem válidos sem eles. */
    checkin: z.iso.date().optional(),
    checkout: z.iso.date().optional(),
    guests: z.number().int().positive().optional(),
  })
  .strict()
  .refine((value) => (value.checkin === undefined) === (value.checkout === undefined), {
    message: 'checkin and checkout must both be present or both be absent',
  });
export type HotelPlaceOption = z.infer<typeof HotelPlaceOptionSchema>;

export const HotelPlacesResponseSchema = z.object({
  items: z.array(HotelPlaceOptionSchema),
});
export type HotelPlacesResponse = z.infer<typeof HotelPlacesResponseSchema>;

/**
 * Wire do `GET /hotel/places/suggestions` (port `hotelPlaceSuggestions`) — SÓ
 * schema/tipos, sem React, para o barrel puro poder re-exportar. Mesma
 * `HotelPlaceOption` do `GET /hotel/places?q`: o cliente NÃO aprende um segundo
 * formato de lugar, só um segundo jeito de obter a lista.
 *
 * São DOIS conjuntos porque são duas PROCEDÊNCIAS: `recent` é histórico do
 * viajante (o que ele BUSCOU), `favorite` é a lista que o PRÓPRIO viajante
 * escolheu guardar — não mais os escritórios da empresa (não há mais escopo
 * de empresa nesta store). Ambos podem vir vazios — sugestão é conveniência,
 * e a rota é fail-open por fonte.
 *
 * Rota própria (não `?q=` vazio) espelhando `GET /flight/airports/history`:
 * `HotelPlacesQuerySchema` exige 2 chars, então `q` vazio é 400 por contrato.
 */
export const HotelPlaceSuggestionsResponseSchema = z.object({
  recent: z.array(HotelPlaceOptionSchema),
  favorite: z.array(HotelPlaceOptionSchema),
});
export type HotelPlaceSuggestions = z.infer<typeof HotelPlaceSuggestionsResponseSchema>;

/**
 * Port das sugestões pré-digitação — injetado pelo host via
 * `GenUiConfig.ports.hotelPlaceSuggestions`. Query-only, sem argumentos (o escopo
 * é o usuário do token). Ausente ⇒ o campo de destino segue como hoje (só busca
 * remota por texto); é o pré-preenchimento, nunca um requisito.
 */
export interface HotelPlaceSuggestionsPort {
  getHotelPlaceSuggestions: () => Promise<HotelPlaceSuggestions>;
}

export interface HotelPlannerPort {
  searchHotelPlaces: (q: string) => Promise<HotelPlaceOption[]>;
  /** "Minha localização": a posição do navegador trocada por UM lugar real
   *  (`GET /hotel/places?latitude&longitude`) — a MESMA linha de um pick por
   *  texto, com `ref`; o pick hidrata pelo caminho comum. `null` quando o ponto
   *  não geocodifica. OPCIONAL: sem ele a ação "perto de mim" não existe. */
  findHotelPlaceByCoordinate?: (coordinate: {
    latitude: number;
    longitude: number;
  }) => Promise<HotelPlaceOption | null>;
  /** `sessionId` refina DENTRO da sessão que produziu as ofertas em cena, em
   *  vez de mintar uma nova a cada busca — é o que mantém o offerId escolhido
   *  resolvível pelo agente. Ausente: o host minta e devolve a que usou. */
  searchHotels: (
    selection: HotelPlannerSelection,
    signal?: AbortSignal,
    sessionId?: string,
  ) => Promise<HotelSearchPreviewResult>;
  /** Detalhe hotel-level da oferta (GET /hotel/details) pro drawer de detalhes. */
  getHotelDetails: (offerId: string, signal?: AbortSignal) => Promise<HotelDetailsResult>;
  /** Uma página da lista COMPLETA da sessão (POST /hotel/search-page) — a
   *  superfície do scroll infinito do painel "ver todos". OPCIONAL: sem ela o
   *  painel degrada para as ofertas que o bloco trouxe, que é o que ele mostra
   *  hoje. Os cortes viajam porque o fornecedor os aplica sobre o conjunto
   *  inteiro; filtrar página carregada é filtrar o pedaço errado.
   *
   *  Convenção de falha: rejeitar com um erro cujo `name` seja
   *  `HOTEL_SEARCH_STALE` diz "a busca desta sessão venceu" — o painel volta
   *  para as ofertas do bloco em vez de acusar falha do fornecedor, porque não
   *  há o que repetir: o conjunto não existe mais e o caminho é refazer a busca
   *  na faixa. Qualquer outra rejeição é falha de leitura, com aviso e repetir. */
  searchHotelPage?: (
    input: HotelResultsPageRequest,
    signal?: AbortSignal,
  ) => Promise<HotelResultsPageResult>;
}
